import React, { useEffect, useState, useRef } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import api from '../services/api';
import { swal } from '../utils/swal';
import './Operadores.css';

interface Operador {
  id: number;
  tipo_documento: string;
  numero_documento: string;
  nombres: string;
  apellidos: string;
  estado: string;
  acuerdo_firmado: boolean;
  celular?: string | null;
  vehiculo_id?: number | null;
  vehiculo_placa?: string;
  vehiculo_marca?: string;
  vehiculo_modelo?: string;
  vehiculo_numero_interno?: string;
}

interface DocumentoOperador {
  id: number;
  tipo_documento_nombre: string;
  numero_documento: string;
  fecha_emision: string;
  fecha_vencimiento: string;
  estado: string;
  created_at: string;
}

const Operadores: React.FC = () => {
  const [operadores, setOperadores] = useState<Operador[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDocumentosModal, setShowDocumentosModal] = useState(false);
  const [documentosOperador, setDocumentosOperador] = useState<DocumentoOperador[]>([]);
  const [operadorSeleccionado, setOperadorSeleccionado] = useState<Operador | null>(null);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const [showEditarHojaVidaModal, setShowEditarHojaVidaModal] = useState(false);
  const [showCambiarVehiculoModal, setShowCambiarVehiculoModal] = useState(false);
  const [vehiculos, setVehiculos] = useState<any[]>([]);
  const [hojaVidaData, setHojaVidaData] = useState<any>(null);
  const [nuevoVehiculoId, setNuevoVehiculoId] = useState<string>('');
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [showCrearModal, setShowCrearModal] = useState(false);
  const [operadorEditando, setOperadorEditando] = useState<Operador | null>(null);
  const [vehiculoAsociado, setVehiculoAsociado] = useState<any>(null);
  const [formData, setFormData] = useState({
    tipo_documento: 'CC',
    numero_documento: '',
    lugar_expedicion_documento: '',
    nombres: '',
    apellidos: '',
    fecha_nacimiento: '',
    telefono: '',
    celular: '',
    email: '',
    direccion: '',
    ciudad: '',
    departamento: '',
    afp: '',
    eps: '',
    arl: '',
    numero_cuenta_bancaria: '',
    tipo_cuenta_bancaria: 'AHORROS',
    banco: '',
    categoria_licencia: '',
    lugar_expedicion_licencia: ''
  });
  const [fotoCara, setFotoCara] = useState<string | null>(null);
  const [fotoCaraFile, setFotoCaraFile] = useState<File | null>(null);
  const [firmaDigital, setFirmaDigital] = useState('');
  const [firmaManuscrita, setFirmaManuscrita] = useState<string | null>(null);
  const [mostrarCanvasFirma, setMostrarCanvasFirma] = useState(false);
  const signatureRef = useRef<SignatureCanvas>(null);
  
  // Forzar re-render cuando cambie firmaManuscrita
  useEffect(() => {
    if (firmaManuscrita && showEditarModal) {
      console.log('🔄 Firma manuscrita detectada en modal:', {
        tieneFirma: !!firmaManuscrita,
        longitud: firmaManuscrita.length,
        preview: firmaManuscrita.substring(0, 50) + '...'
      });
    }
  }, [firmaManuscrita, showEditarModal]);
  const [showVerModal, setShowVerModal] = useState(false);
  const [operadorCompleto, setOperadorCompleto] = useState<any>(null);

  useEffect(() => {
    loadOperadores();
  }, []);

  const loadOperadores = async () => {
    try {
      const response = await api.get('/operadores', {
        params: { limit: 50, ...(search && { search }) },
      });
      setOperadores(response.data.data || []);
    } catch (error) {
      console.error('Error cargando operadores:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarDocumentosOperador = async (operadorId: number) => {
    try {
      setLoadingDocumentos(true);
      const response = await api.get('/documentos', {
        params: {
          entidad_tipo: 'operador',
          entidad_id: operadorId
        }
      });
      
      // Filtrar solo documentos subidos por el operador (excluir hoja de vida y acuerdo)
      const todosDocumentos = response.data.data || [];
      const documentosFiltrados = todosDocumentos.filter((doc: DocumentoOperador) => {
        const nombre = doc.tipo_documento_nombre || '';
        return !nombre.toLowerCase().includes('hoja de vida') &&
               !nombre.toLowerCase().includes('acuerdo de confidencialidad');
      });
      
      setDocumentosOperador(documentosFiltrados);
    } catch (error) {
      console.error('Error cargando documentos del operador:', error);
      setDocumentosOperador([]);
    } finally {
      setLoadingDocumentos(false);
    }
  };

  const verOperadorCompleto = async (operador: Operador) => {
    try {
      const response = await api.get(`/operadores/${operador.id}`);
      setOperadorCompleto(response.data);
      setShowVerModal(true);
    } catch (error: any) {
      await swal.error(error.response?.data?.error || 'Error al cargar datos del operador');
    }
  };

  const verDocumentos = (operador: Operador) => {
    setOperadorSeleccionado(operador);
    setShowDocumentosModal(true);
    cargarDocumentosOperador(operador.id);
  };


  const abrirCambiarVehiculo = async (operador: Operador) => {
    setOperadorSeleccionado(operador);
    setNuevoVehiculoId(operador.vehiculo_id?.toString() || '');
    try {
      // Cargar lista de vehículos disponibles
      const response = await api.get('/vehiculos', { params: { limit: 1000, estado: 'activo' } });
      setVehiculos(response.data.data || []);
      setShowCambiarVehiculoModal(true);
    } catch (error) {
      console.error('Error cargando vehículos:', error);
      setVehiculos([]);
      setShowCambiarVehiculoModal(true);
    }
  };

  const guardarHojaVida = async () => {
    if (!operadorSeleccionado || !hojaVidaData) return;
    
    try {
      await api.put(`/operadores/${operadorSeleccionado.id}/hoja-vida`, hojaVidaData);
      await swal.success('Hoja de vida actualizada correctamente');
      setShowEditarHojaVidaModal(false);
      loadOperadores();
    } catch (error: any) {
      await swal.error(error.response?.data?.error || 'Error al actualizar hoja de vida');
    }
  };

  const guardarCambioVehiculo = async () => {
    if (!operadorSeleccionado) return;
    
    try {
      await api.put(`/operadores/${operadorSeleccionado.id}/vehiculo`, {
        vehiculo_id: nuevoVehiculoId ? parseInt(nuevoVehiculoId) : null
      });
      await swal.success('Vehículo actualizado correctamente');
      setShowCambiarVehiculoModal(false);
      loadOperadores();
    } catch (error: any) {
      await swal.error(error.response?.data?.error || 'Error al cambiar vehículo');
    }
  };

  const abrirEditarOperador = async (operador: Operador) => {
    try {
      // Cargar datos completos del operador
      const response = await api.get(`/operadores/${operador.id}`);
      const operadorCompleto = response.data;
      
      // Guardar vehículo asociado si existe
      if (operadorCompleto.vehiculo) {
        setVehiculoAsociado(operadorCompleto.vehiculo);
      } else {
        setVehiculoAsociado(null);
      }
      
      // Cargar hoja de vida para obtener foto y firma
      let fotoCaraUrl: string | null = null;
      let firmaDigitalData = '';
      let firmaManuscritaData: string | null = null;
      let hojaVidaData: any = null;
      
      try {
        const hojaVidaResponse = await api.get(`/documentos/operador/${operador.id}/hoja-vida`);
        hojaVidaData = hojaVidaResponse.data;
        
        // Obtener foto desde hoja de vida
        if (hojaVidaData.datos_personales?.foto_cara) {
          const fotoPath = hojaVidaData.datos_personales.foto_cara;
          if (fotoPath.startsWith('/uploads/')) {
            // Es una ruta, construir URL completa con timestamp para evitar caché
            fotoCaraUrl = `${window.location.origin}${fotoPath}?t=${Date.now()}`;
          } else if (fotoPath.startsWith('data:image')) {
            // Es base64
            fotoCaraUrl = fotoPath;
          }
          console.log('📷 Foto cargada desde hoja de vida:', !!fotoCaraUrl);
        }
      } catch (e) {
        console.log('No se encontró hoja de vida o foto:', e);
      }
      
      // Obtener firma desde operador - PRIORIDAD: primero desde operador, luego desde hoja de vida
      console.log('🔍 Cargando firma del operador...');
      console.log('  - operadorCompleto.firma_digital existe:', !!operadorCompleto.firma_digital);
      console.log('  - Tipo:', typeof operadorCompleto.firma_digital);
      
      if (operadorCompleto.firma_digital) {
        try {
          let firmaDataParsed;
          if (typeof operadorCompleto.firma_digital === 'string') {
            // Intentar parsear como JSON
            try {
              firmaDataParsed = JSON.parse(operadorCompleto.firma_digital);
              console.log('  - Firma parseada como JSON exitosamente');
            } catch (parseError) {
              // Si no es JSON válido, puede ser solo el texto
              console.log('  - No es JSON válido, usando como texto');
              firmaDigitalData = operadorCompleto.firma_digital;
              firmaDataParsed = null;
            }
          } else {
            firmaDataParsed = operadorCompleto.firma_digital;
            console.log('  - Firma ya es objeto');
          }
          
          if (firmaDataParsed) {
            firmaDigitalData = firmaDataParsed.firma_digital || '';
            firmaManuscritaData = firmaDataParsed.firma_manuscrita || null;
            console.log('  - ✅ Firma cargada desde operador:', { 
              tieneDigital: !!firmaDigitalData, 
              tieneManuscrita: !!firmaManuscritaData,
              longitudManuscrita: firmaManuscritaData ? firmaManuscritaData.length : 0
            });
          }
        } catch (e) {
          console.error('  - ❌ Error procesando firma:', e);
          firmaDigitalData = typeof operadorCompleto.firma_digital === 'string' 
            ? operadorCompleto.firma_digital 
            : '';
        }
      }
      
      // Si no hay firma manuscrita desde operador, intentar desde hoja de vida
      if (!firmaManuscritaData) {
        console.log('  - No hay firma manuscrita desde operador, buscando en hoja de vida...');
        try {
          // Reutilizar hojaVidaData si ya se cargó antes
          if (!hojaVidaData) {
            const hojaVidaResponse = await api.get(`/documentos/operador/${operador.id}/hoja-vida`);
            hojaVidaData = hojaVidaResponse.data;
          }
          if (hojaVidaData?.firma_manuscrita) {
            firmaManuscritaData = hojaVidaData.firma_manuscrita;
            console.log('  - ✅ Firma manuscrita cargada desde hoja de vida');
          } else {
            console.log('  - ⚠️ No se encontró firma manuscrita en hoja de vida');
          }
        } catch (e) {
          console.error('  - ❌ Error cargando firma desde hoja de vida:', e);
        }
      }
      
      // Si aún no hay firma manuscrita, intentar desde documento de acuerdo (igual que el generador de PDF)
      if (!firmaManuscritaData) {
        console.log('  - No hay firma manuscrita desde hoja de vida, buscando en acuerdo de confidencialidad...');
        try {
          const acuerdoResponse = await api.get(`/documentos/operador/${operador.id}/acuerdo`);
          const acuerdoData = acuerdoResponse.data;
          
          if (acuerdoData?.firma_manuscrita) {
            firmaManuscritaData = acuerdoData.firma_manuscrita;
            console.log('  - ✅ Firma manuscrita cargada desde acuerdo de confidencialidad');
          }
          
          // También actualizar firma digital si no estaba
          if (!firmaDigitalData && acuerdoData?.firma_digital) {
            firmaDigitalData = acuerdoData.firma_digital;
            console.log('  - ✅ Firma digital cargada desde acuerdo de confidencialidad');
          }
        } catch (e) {
          // No es error crítico si no hay acuerdo
          console.log('  - ⚠️ No se encontró acuerdo de confidencialidad o error al cargarlo:', e);
        }
      }
      
      // Si aún no hay firma, intentar desde postulación (última opción, igual que el generador de PDF)
      if (!firmaManuscritaData) {
        console.log('  - No hay firma manuscrita desde acuerdo, buscando en postulación...');
        try {
          const postulacionResponse = await api.get(`/postulaciones?operador_id=${operador.id}&estado=aprobado&limit=1`);
          const postulaciones = postulacionResponse.data.data || [];
          
          if (postulaciones.length > 0 && postulaciones[0].firma_digital) {
            let firmaRaw = postulaciones[0].firma_digital;
            
            if (typeof firmaRaw === 'string') {
              // Intentar parsear como JSON
              if (firmaRaw.trim().startsWith('{')) {
                try {
                  const firmaParsed = JSON.parse(firmaRaw);
                  if (firmaParsed.firma_manuscrita) {
                    firmaManuscritaData = firmaParsed.firma_manuscrita;
                    console.log('  - ✅ Firma manuscrita cargada desde postulación (JSON)');
                  }
                  if (!firmaDigitalData && firmaParsed.firma_digital) {
                    firmaDigitalData = firmaParsed.firma_digital;
                  }
                } catch (e) {
                  console.log('  - Error parseando firma desde postulación:', e);
                }
              } else if (firmaRaw.includes('data:image')) {
                // Si es base64 directo, usarlo
                firmaManuscritaData = firmaRaw;
                console.log('  - ✅ Firma manuscrita cargada desde postulación (base64 directo)');
              }
            } else if (typeof firmaRaw === 'object' && firmaRaw.firma_manuscrita) {
              firmaManuscritaData = firmaRaw.firma_manuscrita;
              console.log('  - ✅ Firma manuscrita cargada desde postulación (objeto)');
            }
          }
        } catch (e) {
          console.log('  - ⚠️ No se encontró postulación o error al cargarla:', e);
        }
      }
      
      console.log('📋 Estado final de firma:', {
        firmaDigital: !!firmaDigitalData,
        firmaManuscrita: !!firmaManuscritaData
      });
      
      setOperadorEditando(operador);
      setFormData({
        tipo_documento: operadorCompleto.tipo_documento || 'CC',
        numero_documento: operadorCompleto.numero_documento || '',
        lugar_expedicion_documento: operadorCompleto.lugar_expedicion_documento || '',
        nombres: operadorCompleto.nombres || '',
        apellidos: operadorCompleto.apellidos || '',
        fecha_nacimiento: operadorCompleto.fecha_nacimiento ? operadorCompleto.fecha_nacimiento.split('T')[0] : '',
        telefono: operadorCompleto.telefono || '',
        celular: operadorCompleto.celular || '',
        email: operadorCompleto.email || '',
        direccion: operadorCompleto.direccion || '',
        ciudad: operadorCompleto.ciudad || '',
        departamento: operadorCompleto.departamento || '',
        afp: operadorCompleto.afp || '',
        eps: operadorCompleto.eps || '',
        arl: operadorCompleto.arl || '',
        numero_cuenta_bancaria: operadorCompleto.numero_cuenta_bancaria || '',
        tipo_cuenta_bancaria: operadorCompleto.tipo_cuenta_bancaria || 'AHORROS',
        banco: operadorCompleto.banco || '',
        categoria_licencia: operadorCompleto.categoria_licencia || '',
        lugar_expedicion_licencia: operadorCompleto.lugar_expedicion_licencia || ''
      });
      // IMPORTANTE: Limpiar estados primero para evitar valores residuales
      setFotoCara(null);
      setFotoCaraFile(null);
      setFirmaDigital('');
      setFirmaManuscrita(null);
      setMostrarCanvasFirma(false);
      
      // Pequeño delay para asegurar que los estados se limpien
      await new Promise(resolve => setTimeout(resolve, 10));
      
      // Ahora establecer los nuevos valores
      setFotoCara(fotoCaraUrl);
      setFotoCaraFile(null);
      setFirmaDigital(firmaDigitalData);
      setFirmaManuscrita(firmaManuscritaData); // Esto debe establecerse para que el modal muestre la firma
      setMostrarCanvasFirma(false); // No mostrar canvas inicialmente
      
      console.log('📋 Estado inicializado antes de abrir modal:', {
        fotoCara: !!fotoCaraUrl,
        firmaDigital: !!firmaDigitalData,
        firmaManuscrita: !!firmaManuscritaData,
        firmaManuscritaLength: firmaManuscritaData ? firmaManuscritaData.length : 0,
        firmaManuscritaPreview: firmaManuscritaData ? firmaManuscritaData.substring(0, 50) + '...' : 'null',
        firmaManuscritaType: typeof firmaManuscritaData,
        firmaManuscritaIsString: typeof firmaManuscritaData === 'string',
        firmaManuscritaIsTruthy: !!firmaManuscritaData
      });
      
      // Delay para asegurar que el estado se establezca antes de renderizar el modal
      await new Promise(resolve => setTimeout(resolve, 100));
      
      setShowEditarModal(true);
      
      // NO cargar firma en canvas si ya existe - solo mostrar la imagen
      // El canvas solo se usa cuando NO hay firma guardada
    } catch (error: any) {
      await swal.error(error.response?.data?.error || 'Error al cargar datos del operador');
    }
  };

  const guardarNuevoOperador = async () => {
    // Validar campos requeridos
    if (!formData.tipo_documento || !formData.numero_documento || !formData.nombres || !formData.apellidos) {
      await swal.error('Por favor complete todos los campos requeridos (Tipo de Documento, Número de Documento, Nombres y Apellidos)');
      return;
    }
    
    try {
      // Crear FormData para enviar archivos si hay foto
      const formDataToSend = new FormData();
      
      // Agregar todos los campos del formulario
      Object.keys(formData).forEach(key => {
        const value = (formData as any)[key];
        if (value !== undefined) {
          formDataToSend.append(key, value === null ? '' : String(value));
        }
      });
      
      // Agregar foto si hay una
      if (fotoCaraFile) {
        formDataToSend.append('foto_cara', fotoCaraFile);
      }
      
      // Agregar firma si existe
      const firmaData = {
        firma_digital: firmaDigital || '',
        firma_manuscrita: firmaManuscrita || null
      };
      formDataToSend.append('firma_digital', JSON.stringify(firmaData));
      
      console.log('Creando nuevo operador:', {
        tipo_documento: formData.tipo_documento,
        numero_documento: formData.numero_documento,
        nombres: formData.nombres,
        apellidos: formData.apellidos
      });
      
      await api.post('/operadores', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      await swal.success('Operador creado correctamente');
      
      // Limpiar formulario
      setFormData({
        tipo_documento: 'CC',
        numero_documento: '',
        lugar_expedicion_documento: '',
        nombres: '',
        apellidos: '',
        fecha_nacimiento: '',
        telefono: '',
        celular: '',
        email: '',
        direccion: '',
        ciudad: '',
        departamento: '',
        afp: '',
        eps: '',
        arl: '',
        numero_cuenta_bancaria: '',
        tipo_cuenta_bancaria: 'AHORROS',
        banco: '',
        categoria_licencia: '',
        lugar_expedicion_licencia: ''
      });
      setFotoCara(null);
      setFotoCaraFile(null);
      setFirmaDigital('');
      setFirmaManuscrita(null);
      setMostrarCanvasFirma(false);
      if (signatureRef.current) {
        signatureRef.current.clear();
      }
      setShowCrearModal(false);
      
      // Recargar lista de operadores
      loadOperadores();
    } catch (error: any) {
      console.error('Error al crear operador:', error);
      await swal.error(error.response?.data?.error || 'Error al crear operador');
    }
  };

  const guardarEdicionOperador = async () => {
    if (!operadorEditando) return;
    
    // Validar campos requeridos
    if (!formData.tipo_documento || !formData.numero_documento || !formData.nombres || !formData.apellidos) {
      await swal.error('Por favor complete todos los campos requeridos (Tipo de Documento, Número de Documento, Nombres y Apellidos)');
      return;
    }
    
    try {
      // Crear FormData para enviar archivos si hay foto nueva
      const formDataToSend = new FormData();
      
      // Agregar todos los campos del formulario
      Object.keys(formData).forEach(key => {
        const value = (formData as any)[key];
        // Enviar el valor incluso si está vacío (excepto undefined)
        if (value !== undefined) {
          // Convertir null a string vacío para FormData
          formDataToSend.append(key, value === null ? '' : String(value));
        }
      });
      
      // Agregar vehiculo_id si existe en el operador
      if (operadorEditando.vehiculo_id) {
        formDataToSend.append('vehiculo_id', String(operadorEditando.vehiculo_id));
      } else {
        formDataToSend.append('vehiculo_id', '');
      }
      
      // Agregar foto si hay una nueva
      if (fotoCaraFile) {
        formDataToSend.append('foto_cara', fotoCaraFile);
      }
      
      // Agregar firma si existe (siempre enviar, incluso si está vacía para actualizar)
      const firmaData = {
        firma_digital: firmaDigital || '',
        firma_manuscrita: firmaManuscrita || null
      };
      formDataToSend.append('firma_digital', JSON.stringify(firmaData));
      
      console.log('Enviando actualización de operador:', {
        id: operadorEditando.id,
        tipo_documento: formData.tipo_documento,
        numero_documento: formData.numero_documento,
        nombres: formData.nombres,
        apellidos: formData.apellidos
      });
      
      await api.put(`/operadores/${operadorEditando.id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      await swal.success('Operador actualizado correctamente');
      
      // Recargar datos del operador para actualizar la vista
      if (fotoCaraFile) {
        // Si se subió una nueva foto, recargar desde el servidor
        try {
          const hojaVidaResponse = await api.get(`/documentos/operador/${operadorEditando.id}/hoja-vida`);
          const hojaVidaData = hojaVidaResponse.data;
          if (hojaVidaData.datos_personales?.foto_cara) {
            const fotoPath = hojaVidaData.datos_personales.foto_cara;
            if (fotoPath.startsWith('/uploads/')) {
              // Agregar timestamp para forzar recarga
              const urlWithTimestamp = `${window.location.origin}${fotoPath}?t=${Date.now()}`;
              setFotoCara(urlWithTimestamp);
            }
          }
        } catch (e) {
          console.error('Error recargando foto:', e);
        }
      }
      
      setShowEditarModal(false);
      loadOperadores();
    } catch (error: any) {
      console.error('Error al actualizar operador:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || 'Error al actualizar operador';
      await swal.error(errorMessage);
    }
  };

  const eliminarOperador = async (operador: Operador) => {
    const result = await swal.confirm(
      `¿Estás seguro de que deseas eliminar al operador ${operador.nombres} ${operador.apellidos}? Esta acción no se puede deshacer.`,
      'Eliminar Operador'
    );
    
    if (result.isConfirmed) {
      try {
        await api.delete(`/operadores/${operador.id}`);
        await swal.success('Operador eliminado correctamente');
        loadOperadores();
      } catch (error: any) {
        await swal.error(error.response?.data?.error || 'Error al eliminar operador');
      }
    }
  };

  const getEstadoBadge = (estado: string) => {
    const badges: { [key: string]: { text: string; class: string } } = {
      aprobado: { text: 'Aprobado', class: 'badge-success' },
      pendiente: { text: 'Pendiente', class: 'badge-warning' },
      en_subsanacion: { text: 'En Subsanación', class: 'badge-info' },
      rechazado: { text: 'Rechazado', class: 'badge-danger' },
      inactivo: { text: 'Inactivo', class: 'badge-secondary' },
    };
    const badge = badges[estado] || { text: estado, class: 'badge-secondary' };
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) {
    return <div className="loading">Cargando operadores...</div>;
  }

  return (
    <div className="operadores-page">
      <div className="page-header">
        <div className="header-actions">
          <input
            type="text"
            placeholder="Buscar operador..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && loadOperadores()}
            className="search-input"
          />
          <button onClick={loadOperadores} className="btn-primary">
            🔍 Buscar
          </button>
          <button onClick={() => {
            // Limpiar formulario
            setFormData({
              tipo_documento: 'CC',
              numero_documento: '',
              lugar_expedicion_documento: '',
              nombres: '',
              apellidos: '',
              fecha_nacimiento: '',
              telefono: '',
              celular: '',
              email: '',
              direccion: '',
              ciudad: '',
              departamento: '',
              afp: '',
              eps: '',
              arl: '',
              numero_cuenta_bancaria: '',
              tipo_cuenta_bancaria: 'AHORROS',
              banco: '',
              categoria_licencia: '',
              lugar_expedicion_licencia: ''
            });
            setFotoCara(null);
            setFotoCaraFile(null);
            setFirmaDigital('');
            setFirmaManuscrita(null);
            setMostrarCanvasFirma(false);
            setVehiculoAsociado(null);
            if (signatureRef.current) {
              signatureRef.current.clear();
            }
            setShowCrearModal(true);
          }} className="btn-primary">
            ➕ Nuevo Operador
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Documento</th>
              <th>Nombre Completo</th>
              <th>Celular</th>
              <th>Estado</th>
              <th>Acuerdo Firmado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {operadores.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  No hay operadores registrados
                </td>
              </tr>
            ) : (
              operadores.map((operador) => (
                <tr key={operador.id}>
                  <td>{operador.tipo_documento} {operador.numero_documento}</td>
                  <td>{operador.nombres} {operador.apellidos}</td>
                  <td>
                    {operador.celular ? (
                      <a
                        href={`https://wa.me/57${operador.celular.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#25D366',
                          textDecoration: 'none',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          fontWeight: '500'
                        }}
                        title="Abrir en WhatsApp"
                      >
                        📱 {operador.celular}
                      </a>
                    ) : (
                      <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>-</span>
                    )}
                  </td>
                  <td>{getEstadoBadge(operador.estado)}</td>
                  <td>
                    {operador.acuerdo_firmado ? (
                      <span className="badge badge-success">✓ Firmado</span>
                    ) : (
                      <span className="badge badge-warning">Pendiente</span>
                    )}
                  </td>
                  <td>
                    {operador.vehiculo_placa && (
                      <div className="vehiculo-info" style={{ marginBottom: '5px', fontSize: '0.9em', color: '#666' }}>
                        🚛 {operador.vehiculo_placa} - {operador.vehiculo_marca} {operador.vehiculo_modelo}
                      </div>
                    )}
                    <div className="action-buttons">
                      <button 
                        onClick={() => verDocumentos(operador)}
                        className="btn-link"
                      >
                        📄 Documentos
                      </button>
                      <button 
                        onClick={() => abrirCambiarVehiculo(operador)}
                        className="btn-link"
                        title="Cambiar vehículo asignado"
                      >
                        🚛 Vehículo
                      </button>
                      <button 
                        onClick={() => verOperadorCompleto(operador)}
                        className="btn-link"
                        title="Ver todos los datos del operador"
                      >
                        👁️ Ver
                      </button>
                      <button 
                        onClick={() => abrirEditarOperador(operador)}
                        className="btn-link"
                        title="Editar todos los datos del operador"
                        style={{ fontWeight: '600' }}
                      >
                        ✏️ Editar
                      </button>
                      <button 
                        onClick={() => eliminarOperador(operador)}
                        className="btn-link"
                        style={{ color: '#dc3545' }}
                        title="Eliminar operador"
                      >
                        🗑️ Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de documentos del operador */}
      {showDocumentosModal && operadorSeleccionado && (
        <div className="modal-overlay" onClick={() => setShowDocumentosModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Documentos de {operadorSeleccionado.nombres} {operadorSeleccionado.apellidos}</h2>
              <button 
                className="modal-close"
                onClick={() => setShowDocumentosModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {loadingDocumentos ? (
                <div className="loading">Cargando documentos...</div>
              ) : documentosOperador.length === 0 ? (
                <div className="empty-state">
                  <p>Este operador no ha subido documentos aún.</p>
                </div>
              ) : (
                <div className="documentos-list-modal">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Tipo de Documento</th>
                        <th>Número</th>
                        <th>Fecha Emisión</th>
                        <th>Fecha Vencimiento</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentosOperador.map((doc) => (
                        <tr key={doc.id}>
                          <td>{doc.tipo_documento_nombre}</td>
                          <td>{doc.numero_documento || 'N/A'}</td>
                          <td>{doc.fecha_emision ? new Date(doc.fecha_emision).toLocaleDateString() : 'N/A'}</td>
                          <td>{doc.fecha_vencimiento ? new Date(doc.fecha_vencimiento).toLocaleDateString() : 'N/A'}</td>
                          <td>{getEstadoBadge(doc.estado)}</td>
                          <td>
                            <button
                              className="btn-link"
                              onClick={async () => {
                                try {
                                  const response = await api.get(`/documentos/${doc.id}/pdf`, {
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `documento-${doc.id}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                } catch (error) {
                                  console.error('Error generando PDF:', error);
                                  await swal.error('Error al generar PDF');
                                }
                              }}
                            >
                              📄 PDF
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar hoja de vida */}
      {showEditarHojaVidaModal && operadorSeleccionado && hojaVidaData && (
        <div className="modal-overlay" onClick={() => setShowEditarHojaVidaModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar Hoja de Vida - {operadorSeleccionado.nombres} {operadorSeleccionado.apellidos}</h2>
              <button 
                className="modal-close"
                onClick={() => setShowEditarHojaVidaModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Datos Personales</label>
                <textarea
                  value={JSON.stringify(hojaVidaData.datos_personales || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setHojaVidaData({ ...hojaVidaData, datos_personales: parsed });
                    } catch (e) {
                      // Ignorar errores de parsing mientras se escribe
                    }
                  }}
                  rows={10}
                  style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
                />
              </div>

              <div className="form-group">
                <label>Experiencia Laboral</label>
                <textarea
                  value={JSON.stringify(hojaVidaData.experiencia_laboral || [], null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setHojaVidaData({ ...hojaVidaData, experiencia_laboral: parsed });
                    } catch (e) {
                      // Ignorar errores de parsing mientras se escribe
                    }
                  }}
                  rows={10}
                  style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
                />
              </div>

              <div className="form-group">
                <label>Estudios</label>
                <textarea
                  value={JSON.stringify(hojaVidaData.estudios || [], null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setHojaVidaData({ ...hojaVidaData, estudios: parsed });
                    } catch (e) {
                      // Ignorar errores de parsing mientras se escribe
                    }
                  }}
                  rows={10}
                  style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
                />
              </div>

              <div className="form-group">
                <label>Referencias</label>
                <textarea
                  value={JSON.stringify(hojaVidaData.referencias || [], null, 2)}
                  onChange={(e) => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      setHojaVidaData({ ...hojaVidaData, referencias: parsed });
                    } catch (e) {
                      // Ignorar errores de parsing mientras se escribe
                    }
                  }}
                  rows={10}
                  style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
                />
              </div>

              <div className="modal-actions">
                <button onClick={guardarHojaVida} className="btn-primary">
                  💾 Guardar Cambios
                </button>
                <button onClick={() => setShowEditarHojaVidaModal(false)} className="btn-secondary">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para crear operador */}
      {showCrearModal && (
        <div className="modal-overlay" onClick={() => {
          setFotoCara(null);
          setFotoCaraFile(null);
          setFirmaDigital('');
          setFirmaManuscrita(null);
          setMostrarCanvasFirma(false);
          if (signatureRef.current) {
            signatureRef.current.clear();
          }
          setShowCrearModal(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Crear Nuevo Operador</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setFotoCara(null);
                  setFotoCaraFile(null);
                  setFirmaDigital('');
                  setFirmaManuscrita(null);
                  if (signatureRef.current) {
                    signatureRef.current.clear();
                  }
                  setShowCrearModal(false);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Tipo de Documento *</label>
                  <select
                    value={formData.tipo_documento}
                    onChange={(e) => setFormData({ ...formData, tipo_documento: e.target.value })}
                    required
                  >
                    <option value="CC">CC</option>
                    <option value="CE">CE</option>
                    <option value="PASAPORTE">PASAPORTE</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Número de Documento *</label>
                  <input
                    type="text"
                    value={formData.numero_documento}
                    onChange={(e) => setFormData({ ...formData, numero_documento: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Lugar de Expedición</label>
                  <input
                    type="text"
                    value={formData.lugar_expedicion_documento}
                    onChange={(e) => setFormData({ ...formData, lugar_expedicion_documento: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Nombres *</label>
                  <input
                    type="text"
                    value={formData.nombres}
                    onChange={(e) => setFormData({ ...formData, nombres: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Apellidos *</label>
                  <input
                    type="text"
                    value={formData.apellidos}
                    onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Fecha de Nacimiento</label>
                  <input
                    type="date"
                    value={formData.fecha_nacimiento}
                    onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Celular</label>
                  <input
                    type="tel"
                    value={formData.celular}
                    onChange={(e) => setFormData({ ...formData, celular: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Dirección</label>
                  <input
                    type="text"
                    value={formData.direccion}
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Ciudad</label>
                  <input
                    type="text"
                    value={formData.ciudad}
                    onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Departamento</label>
                  <input
                    type="text"
                    value={formData.departamento}
                    onChange={(e) => setFormData({ ...formData, departamento: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>AFP</label>
                  <input
                    type="text"
                    value={formData.afp}
                    onChange={(e) => setFormData({ ...formData, afp: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>EPS</label>
                  <input
                    type="text"
                    value={formData.eps}
                    onChange={(e) => setFormData({ ...formData, eps: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>ARL</label>
                  <input
                    type="text"
                    value={formData.arl}
                    onChange={(e) => setFormData({ ...formData, arl: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Número de Cuenta Bancaria</label>
                  <input
                    type="text"
                    value={formData.numero_cuenta_bancaria}
                    onChange={(e) => setFormData({ ...formData, numero_cuenta_bancaria: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Tipo de Cuenta</label>
                  <select
                    value={formData.tipo_cuenta_bancaria}
                    onChange={(e) => setFormData({ ...formData, tipo_cuenta_bancaria: e.target.value })}
                  >
                    <option value="AHORROS">Ahorros</option>
                    <option value="CORRIENTE">Corriente</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Banco</label>
                  <input
                    type="text"
                    value={formData.banco}
                    onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Categoría de Licencia</label>
                  <input
                    type="text"
                    value={formData.categoria_licencia}
                    onChange={(e) => setFormData({ ...formData, categoria_licencia: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Lugar de Expedición de Licencia</label>
                  <input
                    type="text"
                    value={formData.lugar_expedicion_licencia}
                    onChange={(e) => setFormData({ ...formData, lugar_expedicion_licencia: e.target.value })}
                  />
                </div>
              </div>

              {/* Foto y Firma */}
              <div className="form-section" style={{ marginTop: '20px', borderTop: '2px solid #0f4222', paddingTop: '20px' }}>
                <h3 style={{ marginBottom: '15px', color: '#2c3e50', borderBottom: '2px solid #0f4222', paddingBottom: '10px' }}>
                  📷 Foto y Firma
                </h3>
                
                <div className="form-group">
                  <label>Foto de Cara</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setFotoCaraFile(file);
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFotoCara(reader.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  {fotoCara && (
                    <div style={{ marginTop: '10px' }}>
                      <img src={fotoCara} alt="Foto de cara" style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '5px' }} />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Firma</label>
                  {!firmaManuscrita && !mostrarCanvasFirma && (
                    <button
                      type="button"
                      onClick={() => setMostrarCanvasFirma(true)}
                      className="btn-primary"
                      style={{ marginBottom: '10px' }}
                    >
                      ✍️ Agregar Firma Manuscrita
                    </button>
                  )}
                  
                  {mostrarCanvasFirma && (
                    <div style={{ marginBottom: '10px' }}>
                      <SignatureCanvas
                        ref={signatureRef}
                        canvasProps={{
                          className: 'signature-canvas',
                          style: { width: '100%', height: '150px', border: '1px solid #ccc', borderRadius: '3px', background: 'white' }
                        }}
                      />
                      <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                        <button
                          type="button"
                          onClick={() => {
                            if (signatureRef.current && !signatureRef.current.isEmpty()) {
                              const dataURL = signatureRef.current.toDataURL();
                              setFirmaManuscrita(dataURL);
                              setMostrarCanvasFirma(false);
                            }
                          }}
                          style={{ padding: '5px 10px', background: '#28a745', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                        >
                          💾 Guardar Firma
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (signatureRef.current) {
                              signatureRef.current.clear();
                            }
                            setMostrarCanvasFirma(false);
                          }}
                          style={{ padding: '5px 10px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                        >
                          ✕ Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {firmaManuscrita && !mostrarCanvasFirma && (
                    <div style={{ marginTop: '10px' }}>
                      <img src={firmaManuscrita} alt="Firma manuscrita" style={{ maxWidth: '300px', border: '1px solid #ddd', borderRadius: '5px' }} />
                      <button
                        type="button"
                        onClick={() => {
                          setFirmaManuscrita(null);
                          setMostrarCanvasFirma(true);
                          if (signatureRef.current) {
                            signatureRef.current.clear();
                          }
                        }}
                        className="btn-link"
                        style={{ marginLeft: '10px', color: '#dc3545' }}
                      >
                        ✏️ Editar Firma
                      </button>
                    </div>
                  )}
                  
                  <div className="form-group" style={{ marginTop: '10px' }}>
                    <label>Firma Digital (Texto)</label>
                    <input
                      type="text"
                      value={firmaDigital}
                      onChange={(e) => setFirmaDigital(e.target.value)}
                      placeholder="Ingrese la firma digital (texto)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={() => {
                setFotoCara(null);
                setFotoCaraFile(null);
                setFirmaDigital('');
                setFirmaManuscrita(null);
                setMostrarCanvasFirma(false);
                if (signatureRef.current) {
                  signatureRef.current.clear();
                }
                setShowCrearModal(false);
              }} className="btn-secondary">
                Cancelar
              </button>
              <button onClick={guardarNuevoOperador} className="btn-primary">
                💾 Crear Operador
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para editar operador */}
      {showEditarModal && operadorEditando && (
        <div className="modal-overlay" onClick={() => {
          // Limpiar estados al cerrar el modal
          setFotoCara(null);
          setFotoCaraFile(null);
          setFirmaDigital('');
          setFirmaManuscrita(null);
          setMostrarCanvasFirma(false);
          setVehiculoAsociado(null);
          if (signatureRef.current) {
            signatureRef.current.clear();
          }
          setShowEditarModal(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Editar Operador - {operadorEditando.nombres} {operadorEditando.apellidos}</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  // Limpiar estados al cerrar el modal
                  setFotoCara(null);
                  setFotoCaraFile(null);
                  setFirmaDigital('');
                  setFirmaManuscrita(null);
                  if (signatureRef.current) {
                    signatureRef.current.clear();
                  }
                  setShowEditarModal(false);
                }}
              >
                ✕
              </button>
            </div>

            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Tipo de Documento *</label>
                  <select
                    value={formData.tipo_documento}
                    onChange={(e) => setFormData({ ...formData, tipo_documento: e.target.value })}
                    required
                  >
                    <option value="CC">CC</option>
                    <option value="CE">CE</option>
                    <option value="PASAPORTE">PASAPORTE</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Número de Documento *</label>
                  <input
                    type="text"
                    value={formData.numero_documento}
                    onChange={(e) => setFormData({ ...formData, numero_documento: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Lugar de Expedición</label>
                  <input
                    type="text"
                    value={formData.lugar_expedicion_documento}
                    onChange={(e) => setFormData({ ...formData, lugar_expedicion_documento: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Nombres *</label>
                  <input
                    type="text"
                    value={formData.nombres}
                    onChange={(e) => setFormData({ ...formData, nombres: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Apellidos *</label>
                  <input
                    type="text"
                    value={formData.apellidos}
                    onChange={(e) => setFormData({ ...formData, apellidos: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Fecha de Nacimiento</label>
                  <input
                    type="date"
                    value={formData.fecha_nacimiento}
                    onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Celular</label>
                  <input
                    type="tel"
                    value={formData.celular}
                    onChange={(e) => setFormData({ ...formData, celular: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="form-group full-width">
                  <label>Dirección</label>
                  <input
                    type="text"
                    value={formData.direccion}
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Ciudad</label>
                  <input
                    type="text"
                    value={formData.ciudad}
                    onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Departamento</label>
                  <input
                    type="text"
                    value={formData.departamento}
                    onChange={(e) => setFormData({ ...formData, departamento: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>AFP</label>
                  <input
                    type="text"
                    value={formData.afp}
                    onChange={(e) => setFormData({ ...formData, afp: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>EPS</label>
                  <input
                    type="text"
                    value={formData.eps}
                    onChange={(e) => setFormData({ ...formData, eps: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>ARL</label>
                  <input
                    type="text"
                    value={formData.arl}
                    onChange={(e) => setFormData({ ...formData, arl: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Número de Cuenta Bancaria</label>
                  <input
                    type="text"
                    value={formData.numero_cuenta_bancaria}
                    onChange={(e) => setFormData({ ...formData, numero_cuenta_bancaria: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Tipo de Cuenta</label>
                  <select
                    value={formData.tipo_cuenta_bancaria}
                    onChange={(e) => setFormData({ ...formData, tipo_cuenta_bancaria: e.target.value })}
                  >
                    <option value="AHORROS">Ahorros</option>
                    <option value="CORRIENTE">Corriente</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Banco</label>
                  <input
                    type="text"
                    value={formData.banco}
                    onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Categoría de Licencia</label>
                  <input
                    type="text"
                    value={formData.categoria_licencia}
                    onChange={(e) => setFormData({ ...formData, categoria_licencia: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Lugar de Expedición de Licencia</label>
                  <input
                    type="text"
                    value={formData.lugar_expedicion_licencia}
                    onChange={(e) => setFormData({ ...formData, lugar_expedicion_licencia: e.target.value })}
                  />
                </div>
                
                {/* Foto */}
                <div className="form-group full-width">
                  <label>Foto de Identificación</label>
                  {fotoCara && (
                    <div style={{ marginBottom: '10px' }}>
                      <img src={fotoCara} alt="Foto actual" style={{ maxWidth: '200px', maxHeight: '200px', border: '1px solid #ddd', borderRadius: '5px' }} />
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          swal.error('La foto es demasiado grande. Por favor, use una imagen menor a 5MB');
                          return;
                        }
                        setFotoCaraFile(file);
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          setFotoCara(event.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  {fotoCara && (
                    <button
                      type="button"
                      onClick={() => {
                        setFotoCara(null);
                        setFotoCaraFile(null);
                      }}
                      style={{ marginTop: '10px', padding: '5px 10px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                    >
                      ✕ Eliminar foto
                    </button>
                  )}
                </div>
                
                {/* Firma Digital - Solo lectura */}
                {firmaDigital && (
                  <div className="form-group full-width">
                    <label>Firma Digital (Nombre Completo)</label>
                    <div style={{ 
                      padding: '10px', 
                      background: '#f8f9fa', 
                      border: '1px solid #ddd', 
                      borderRadius: '5px',
                      color: '#495057'
                    }}>
                      {firmaDigital}
                    </div>
                  </div>
                )}
                
                {/* Información del Vehículo Asociado */}
                {vehiculoAsociado && (
                  <div className="form-section" style={{ marginTop: '30px', gridColumn: '1 / -1' }}>
                    <h3 style={{ marginBottom: '15px', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
                      🚛 INFORMACIÓN DEL VEHÍCULO ASOCIADO
                    </h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Placa</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.placa || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Número Interno</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.numero_interno || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Marca</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.marca || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Modelo</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.modelo || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Año</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.año || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Color</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.color || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Tipo de Vehículo</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.tipo_vehiculo || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Repotenciado</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.repotenciado ? 'Sí' : 'No'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Configuración</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.configuracion || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Capacidad de Carga (kg)</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.capacidad_carga || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Carrocería</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.carroceria || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Peso Vacío (kg)</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.peso_vacio || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Número de Motor</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.numero_motor || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Número de Chasis</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.numero_chasis || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Tipo de Combustible</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.tipo_combustible || '-'}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Capacidad del Tanque (Litros)</label>
                        <div style={{ padding: '10px', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '5px', color: '#495057' }}>
                          {vehiculoAsociado.capacidad_tanque || '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Firma Manuscrita */}
                <div className="form-group full-width">
                  <label>Firma Manuscrita</label>
                  {!mostrarCanvasFirma && (firmaManuscrita && typeof firmaManuscrita === 'string' && firmaManuscrita.trim() !== '' && firmaManuscrita.length > 10) ? (
                    <div style={{ 
                      marginBottom: '15px', 
                      padding: '15px', 
                      border: '2px solid #28a745', 
                      borderRadius: '5px', 
                      background: '#f0f9f4' 
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <strong style={{ color: '#28a745', fontSize: '1.1em' }}>✓ Firma Actual del Operador</strong>
                        <button
                          type="button"
                          onClick={() => {
                            setMostrarCanvasFirma(true);
                            if (signatureRef.current) {
                              signatureRef.current.clear();
                            }
                          }}
                          style={{ 
                            padding: '8px 15px', 
                            background: '#007bff', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '5px', 
                            cursor: 'pointer', 
                            fontSize: '0.95em',
                            fontWeight: '600'
                          }}
                        >
                          ✏️ Actualizar Firma
                        </button>
                      </div>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        padding: '10px',
                        background: 'white',
                        borderRadius: '5px',
                        border: '1px solid #ddd'
                      }}>
                        <img 
                          src={firmaManuscrita} 
                          alt="Firma actual del operador" 
                          style={{ 
                            maxWidth: '100%', 
                            maxHeight: '200px', 
                            border: 'none',
                            borderRadius: '3px',
                            display: 'block'
                          }} 
                          onError={(e) => {
                            console.error('Error cargando imagen de firma:', e);
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #ddd', borderRadius: '5px', padding: '10px', background: 'white' }}>
                      {mostrarCanvasFirma && (
                        <>
                          <p style={{ marginBottom: '10px', color: '#666', fontSize: '0.9em' }}>
                            {firmaManuscrita ? 'Dibuje la nueva firma en el área de abajo:' : 'Dibuje su firma en el área de abajo:'}
                          </p>
                          <SignatureCanvas
                            ref={signatureRef}
                            canvasProps={{
                              className: 'signature-canvas',
                              style: { width: '100%', height: '150px', border: '1px solid #ccc', borderRadius: '3px', background: 'white' }
                            }}
                          />
                          <div style={{ marginTop: '10px', display: 'flex', gap: '10px' }}>
                            <button
                              type="button"
                              onClick={() => {
                                if (signatureRef.current) {
                                  signatureRef.current.clear();
                                }
                              }}
                              style={{ padding: '5px 10px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                            >
                              🗑️ Limpiar
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                if (signatureRef.current && !signatureRef.current.isEmpty()) {
                                  const dataURL = signatureRef.current.toDataURL();
                                  setFirmaManuscrita(dataURL);
                                  setMostrarCanvasFirma(false);
                                  await swal.success('Firma guardada correctamente');
                                } else {
                                  await swal.warning('Por favor, dibuje su firma antes de guardar');
                                }
                              }}
                              style={{ padding: '5px 10px', background: '#28a745', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                            >
                              💾 Guardar Firma
                            </button>
                            {firmaManuscrita && (
                              <button
                                type="button"
                                onClick={() => {
                                  setMostrarCanvasFirma(false);
                                  if (signatureRef.current) {
                                    signatureRef.current.clear();
                                  }
                                }}
                                style={{ padding: '5px 10px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
                              >
                                ✕ Cancelar
                              </button>
                            )}
                          </div>
                        </>
                      )}
                      {!mostrarCanvasFirma && !firmaManuscrita && (
                        <div style={{ 
                          padding: '15px', 
                          textAlign: 'center',
                          color: '#6c757d'
                        }}>
                          <p style={{ margin: 0, fontSize: '0.95em', marginBottom: '10px' }}>
                            ⚠️ No hay firma manuscrita registrada para este operador
                          </p>
                          <button
                            type="button"
                            onClick={() => setMostrarCanvasFirma(true)}
                            style={{ 
                              padding: '8px 15px', 
                              background: '#007bff', 
                              color: 'white', 
                              border: 'none', 
                              borderRadius: '5px', 
                              cursor: 'pointer', 
                              fontSize: '0.95em',
                              fontWeight: '600'
                            }}
                          >
                            ➕ Agregar Firma
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button onClick={guardarEdicionOperador} className="btn-primary">
                💾 Guardar Cambios
              </button>
              <button onClick={() => {
                // Limpiar estados al cerrar el modal
                setFotoCara(null);
                setFotoCaraFile(null);
                setFirmaDigital('');
                setFirmaManuscrita(null);
                if (signatureRef.current) {
                  signatureRef.current.clear();
                }
                setShowEditarModal(false);
              }} className="btn-secondary">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para ver operador completo */}
      {showVerModal && operadorCompleto && (
        <div className="modal-overlay" onClick={() => setShowVerModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalles del Operador - {operadorCompleto.nombres} {operadorCompleto.apellidos}</h2>
              <button
                className="modal-close"
                onClick={() => setShowVerModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="detalles-section">
                <h3>📋 Información de Identificación</h3>
                <div className="detalles-grid">
                  <div className="detalle-item">
                    <strong>Tipo de Documento:</strong>
                    <span>{operadorCompleto.tipo_documento || 'N/A'}</span>
                  </div>
                  <div className="detalle-item">
                    <strong>Número de Documento:</strong>
                    <span>{operadorCompleto.numero_documento || 'N/A'}</span>
                  </div>
                  {operadorCompleto.lugar_expedicion_documento && (
                    <div className="detalle-item">
                      <strong>Lugar de Expedición:</strong>
                      <span>{operadorCompleto.lugar_expedicion_documento}</span>
                    </div>
                  )}
                  <div className="detalle-item">
                    <strong>Nombres:</strong>
                    <span>{operadorCompleto.nombres || 'N/A'}</span>
                  </div>
                  <div className="detalle-item">
                    <strong>Apellidos:</strong>
                    <span>{operadorCompleto.apellidos || 'N/A'}</span>
                  </div>
                  {operadorCompleto.fecha_nacimiento && (
                    <div className="detalle-item">
                      <strong>Fecha de Nacimiento:</strong>
                      <span>{new Date(operadorCompleto.fecha_nacimiento).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detalles-section">
                <h3>📞 Información de Contacto</h3>
                <div className="detalles-grid">
                  {operadorCompleto.telefono && (
                    <div className="detalle-item">
                      <strong>Teléfono:</strong>
                      <span>{operadorCompleto.telefono}</span>
                    </div>
                  )}
                  {operadorCompleto.celular && (
                    <div className="detalle-item">
                      <strong>Celular:</strong>
                      <span>{operadorCompleto.celular}</span>
                    </div>
                  )}
                  {operadorCompleto.email && (
                    <div className="detalle-item">
                      <strong>Email:</strong>
                      <span>{operadorCompleto.email}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detalles-section">
                <h3>📍 Información de Ubicación</h3>
                <div className="detalles-grid">
                  {operadorCompleto.direccion && (
                    <div className="detalle-item full-width">
                      <strong>Dirección:</strong>
                      <span>{operadorCompleto.direccion}</span>
                    </div>
                  )}
                  {operadorCompleto.ciudad && (
                    <div className="detalle-item">
                      <strong>Ciudad:</strong>
                      <span>{operadorCompleto.ciudad}</span>
                    </div>
                  )}
                  {operadorCompleto.departamento && (
                    <div className="detalle-item">
                      <strong>Departamento:</strong>
                      <span>{operadorCompleto.departamento}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detalles-section">
                <h3>🏥 Información de Seguridad Social</h3>
                <div className="detalles-grid">
                  {operadorCompleto.afp && (
                    <div className="detalle-item">
                      <strong>AFP:</strong>
                      <span>{operadorCompleto.afp}</span>
                    </div>
                  )}
                  {operadorCompleto.eps && (
                    <div className="detalle-item">
                      <strong>EPS:</strong>
                      <span>{operadorCompleto.eps}</span>
                    </div>
                  )}
                  {operadorCompleto.arl && (
                    <div className="detalle-item">
                      <strong>ARL:</strong>
                      <span>{operadorCompleto.arl}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detalles-section">
                <h3>💰 Información Bancaria</h3>
                <div className="detalles-grid">
                  {operadorCompleto.numero_cuenta_bancaria && (
                    <div className="detalle-item">
                      <strong>Número de Cuenta:</strong>
                      <span>{operadorCompleto.numero_cuenta_bancaria}</span>
                    </div>
                  )}
                  {operadorCompleto.tipo_cuenta_bancaria && (
                    <div className="detalle-item">
                      <strong>Tipo de Cuenta:</strong>
                      <span>{operadorCompleto.tipo_cuenta_bancaria}</span>
                    </div>
                  )}
                  {operadorCompleto.banco && (
                    <div className="detalle-item">
                      <strong>Banco:</strong>
                      <span>{operadorCompleto.banco}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detalles-section">
                <h3>🚗 Información de Licencia de Conducción</h3>
                <div className="detalles-grid">
                  {operadorCompleto.categoria_licencia && (
                    <div className="detalle-item">
                      <strong>Categoría:</strong>
                      <span>{operadorCompleto.categoria_licencia}</span>
                    </div>
                  )}
                  {operadorCompleto.lugar_expedicion_licencia && (
                    <div className="detalle-item">
                      <strong>Lugar de Expedición:</strong>
                      <span>{operadorCompleto.lugar_expedicion_licencia}</span>
                    </div>
                  )}
                </div>
              </div>

              {operadorCompleto.vehiculo && (
                <div className="detalles-section">
                  <h3>🚛 Vehículo Asignado</h3>
                  <div className="detalles-grid">
                    {operadorCompleto.vehiculo.placa && (
                      <div className="detalle-item">
                        <strong>Placa:</strong>
                        <span>{operadorCompleto.vehiculo.placa}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.numero_interno && (
                      <div className="detalle-item">
                        <strong>Número Interno:</strong>
                        <span>{operadorCompleto.vehiculo.numero_interno}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.marca && (
                      <div className="detalle-item">
                        <strong>Marca:</strong>
                        <span>{operadorCompleto.vehiculo.marca}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.modelo && (
                      <div className="detalle-item">
                        <strong>Modelo:</strong>
                        <span>{operadorCompleto.vehiculo.modelo}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.año && (
                      <div className="detalle-item">
                        <strong>Año:</strong>
                        <span>{operadorCompleto.vehiculo.año}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.color && (
                      <div className="detalle-item">
                        <strong>Color:</strong>
                        <span>{operadorCompleto.vehiculo.color}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.tipo_vehiculo && (
                      <div className="detalle-item">
                        <strong>Tipo de Vehículo:</strong>
                        <span>{operadorCompleto.vehiculo.tipo_vehiculo}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.repotenciado !== undefined && (
                      <div className="detalle-item">
                        <strong>Repotenciado:</strong>
                        <span>{operadorCompleto.vehiculo.repotenciado ? 'Sí' : 'No'}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.configuracion && (
                      <div className="detalle-item">
                        <strong>Configuración:</strong>
                        <span>{operadorCompleto.vehiculo.configuracion}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.capacidad_carga && (
                      <div className="detalle-item">
                        <strong>Capacidad de Carga (kg):</strong>
                        <span>{operadorCompleto.vehiculo.capacidad_carga}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.carroceria && (
                      <div className="detalle-item">
                        <strong>Carrocería:</strong>
                        <span>{operadorCompleto.vehiculo.carroceria}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.peso_vacio && (
                      <div className="detalle-item">
                        <strong>Peso Vacío (kg):</strong>
                        <span>{operadorCompleto.vehiculo.peso_vacio}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.numero_motor && (
                      <div className="detalle-item">
                        <strong>Número de Motor:</strong>
                        <span>{operadorCompleto.vehiculo.numero_motor}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.numero_chasis && (
                      <div className="detalle-item">
                        <strong>Número de Chasis:</strong>
                        <span>{operadorCompleto.vehiculo.numero_chasis}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.tipo_combustible && (
                      <div className="detalle-item">
                        <strong>Tipo de Combustible:</strong>
                        <span>{operadorCompleto.vehiculo.tipo_combustible}</span>
                      </div>
                    )}
                    {operadorCompleto.vehiculo.capacidad_tanque && (
                      <div className="detalle-item">
                        <strong>Capacidad Tanque (L):</strong>
                        <span>{operadorCompleto.vehiculo.capacidad_tanque}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {operadorCompleto.documentos && operadorCompleto.documentos.length > 0 && (
                <div className="detalles-section">
                  <h3>📄 Documentos</h3>
                  <div className="detalles-grid">
                    {operadorCompleto.documentos.map((doc: any) => (
                      <div key={doc.id} className="detalle-item full-width">
                        <strong>{doc.tipo_documento_nombre}:</strong>
                        <span>{doc.numero_documento || 'N/A'}</span>
                        {doc.fecha_vencimiento && (
                          <span> - Vence: {new Date(doc.fecha_vencimiento).toLocaleDateString()}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {operadorCompleto.rutas_autorizadas && operadorCompleto.rutas_autorizadas.length > 0 && (
                <div className="detalles-section">
                  <h3>🛣️ Rutas Autorizadas</h3>
                  <div className="detalles-grid">
                    {operadorCompleto.rutas_autorizadas.map((ruta: any) => (
                      <div key={ruta.id} className="detalle-item full-width">
                        <strong>{ruta.ruta_nombre}:</strong>
                        <span>{ruta.origen} → {ruta.destino}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowVerModal(false)} className="btn-secondary">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para cambiar vehículo */}
      {showCambiarVehiculoModal && operadorSeleccionado && (
        <div className="modal-overlay" onClick={() => setShowCambiarVehiculoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cambiar Vehículo - {operadorSeleccionado.nombres} {operadorSeleccionado.apellidos}</h2>
              <button 
                className="modal-close"
                onClick={() => setShowCambiarVehiculoModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {operadorSeleccionado.vehiculo_placa && (
                <div className="info-box" style={{ marginBottom: '20px', padding: '10px', background: '#f0f0f0', borderRadius: '5px' }}>
                  <strong>Vehículo actual:</strong> {operadorSeleccionado.vehiculo_placa} - {operadorSeleccionado.vehiculo_marca} {operadorSeleccionado.vehiculo_modelo}
                </div>
              )}

              <div className="form-group">
                <label>Seleccionar Vehículo</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <select
                    value={nuevoVehiculoId}
                    onChange={(e) => setNuevoVehiculoId(e.target.value)}
                    className="form-select"
                    style={{ flex: 1 }}
                  >
                    <option value="">Sin vehículo asignado</option>
                    {vehiculos
                      .filter(v => !v.operador_id || v.operador_id === operadorSeleccionado.vehiculo_id)
                      .map(vehiculo => (
                        <option key={vehiculo.id} value={vehiculo.id}>
                          {vehiculo.placa} - {vehiculo.marca} {vehiculo.modelo} {vehiculo.numero_interno ? `(${vehiculo.numero_interno})` : ''}
                        </option>
                      ))}
                  </select>
                  {nuevoVehiculoId && (
                    <button
                      onClick={() => {
                        const vehiculo = vehiculos.find(v => v.id.toString() === nuevoVehiculoId);
                        if (vehiculo) {
                          window.open(`/vehiculos`, '_blank');
                        }
                      }}
                      className="btn-link"
                      style={{ whiteSpace: 'nowrap' }}
                      title="Editar vehículo"
                    >
                      ✏️ Editar Vehículo
                    </button>
                  )}
                </div>
                <small>Nota: Solo se muestran vehículos disponibles (no asignados a otros operadores)</small>
              </div>

              <div className="modal-actions">
                <button onClick={guardarCambioVehiculo} className="btn-primary">
                  💾 Guardar Cambio
                </button>
                <button onClick={() => setShowCambiarVehiculoModal(false)} className="btn-secondary">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Operadores;

